import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Video from "../components/Video";
import { V, BREAKDOWN_LABELS } from "../data/site";

function FloatingLabel({ label, at, progress, i }) {
  const opacity = useTransform(
    progress,
    [at - 0.08, at - 0.01, at + 0.09, at + 0.16],
    [0, 1, 1, 0]
  );
  const x = useTransform(progress, [at - 0.08, at + 0.02], [40, 0]);
  return (
    <motion.div
      style={{ opacity, x, top: `${18 + i * 11}%` }}
      className="absolute right-0 flex items-center gap-3"
    >
      <span className="h-px w-10 bg-lav-400/70 md:w-16" />
      <span className="label whitespace-nowrap text-lav-400">{label}</span>
    </motion.div>
  );
}

export default function ContentBreakdown() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end end"],
  });

  return (
    <section ref={ref} className="relative h-[320vh] bg-ink-800" data-testid="content-breakdown">
      <div className="sticky top-0 flex h-[100svh] items-center overflow-hidden px-6 md:px-12 lg:px-16">
        {/* faint atmosphere */}
        <div className="pointer-events-none absolute left-1/2 top-1/2 h-[70vh] w-[70vh] -translate-x-1/2 -translate-y-1/2 rounded-full bg-lav-700/10 blur-[160px]" />

        <div className="relative z-10 grid w-full grid-cols-1 items-center gap-12 lg:grid-cols-2">
          {/* left copy */}
          <div>
            <p className="label text-lav-400">04 — PSYCHOLOGY</p>
            <h2 className="text-huge uppercase mt-4">
              WE DON'T
              <br />
              JUST <span className="text-lav-400">EDIT.</span>
            </h2>
            <p className="mt-8 max-w-md text-sm md:text-base font-light leading-relaxed text-white/55">
              Every second is engineered. We read the viewer — attention, doubt,
              intent — and build the edit around the moments that actually move
              them from watching to buying.
            </p>

            {/* timeline */}
            <div className="mt-12 max-w-md">
              <div className="mb-3 flex justify-between label text-white/40">
                <span>TIMELINE</span>
                <span>SCROLL</span>
              </div>
              <div className="h-[2px] w-full bg-white/10">
                <motion.div style={{ scaleX: scrollYProgress }} className="h-full origin-left bg-lav-400" />
              </div>
            </div>
          </div>

          {/* centered vertical edit with floating labels */}
          <div className="relative mx-auto w-full max-w-[340px]">
            <div className="relative aspect-[9/16] w-full overflow-hidden ring-1 ring-white/10 shadow-[0_0_100px_-30px_rgba(167,139,250,0.5)]">
              <Video src={V.motion2} priority className="h-full w-full" />
              <div className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-lav-400/20" />
            </div>
            {/* labels anchored to the right of the frame */}
            <div className="absolute -right-2 top-0 h-full w-[46%] md:-right-24 md:w-[60%]">
              {BREAKDOWN_LABELS.map((l, i) => (
                <FloatingLabel key={l.label} label={l.label} at={l.at} progress={scrollYProgress} i={i} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
